/* eslint-disable no-console -- depends on console */
import type { LoggerConfiguration } from '../logger.types'
import { BaseLogger } from './base.logger'

// must declare document to prevent document !== 'undefined' from throwing an 'expression is always true error
// document type, but we only `documentMode` so define type like this since we only use this and this doesn't exist on `Document` type
declare const document: { documentMode?: number }
declare const navigator: Navigator | undefined

// Colors taken from _colors.scss to match OCG themes
const messagePrefixColor = '#0068c8' // $blue
const traceMessageColor = '#bcc0d0' // $gray
const debugMessageColor = '#dce1e4' //  $grey-lightest
const infoMessageColor = '#19aaff;' // $blue-light
const warnMessageColor = '#d6ba47' // $yellow-dark
const errorMessageColor = '#ff4955' // $red-light

export class ConsoleLogger extends BaseLogger {
    private isIE = typeof document !== 'undefined' && !!document.documentMode
    private isEdge = typeof navigator !== 'undefined' && !!navigator.userAgent && /Edge/.test(navigator.userAgent)

    /**
     * browser ability to apply styles to log message
     */
    private canStyleLogMessage = !(this.isIE || this.isEdge)

    public trace(tags: string[], message: string, args: unknown[]): void {
        // Use debug console log even for trace as console.trace automatically expands and is harder to read.
        console.debug(...this.formatConsoleLog(tags, message, args, traceMessageColor, '\u{1F58D}', 'yellow'))
    }

    public debug(tags: string[], message: string, args: unknown[]): void {
        console.debug(...this.formatConsoleLog(tags, message, args, debugMessageColor, '\u{1F41E}', 'red'))
    }

    public info(tags: string[], message: string, args: unknown[]): void {
        console.info(...this.formatConsoleLog(tags, message, args, infoMessageColor, '\u{1f4ce}', 'white'))
    }

    public warn(tags: string[], message: string, args: unknown[]): void {
        console.warn(...this.formatConsoleLog(tags, message, args, warnMessageColor))
    }

    public error(tags: string[], message: string, args: unknown[]): void {
        console.error(...this.formatConsoleLog(tags, message, args, errorMessageColor))
    }

    protected getLogLevel([configuration]: LoggerConfiguration) {
        return configuration.consoleLogLevel
    }

    private formatConsoleLog(
        tags: string[],
        message: string,
        args: unknown[] = [],
        messageColor: string,
        prefixIcon: string = '',
        iconColor: string = messageColor
    ) {
        const prefix = tags.join(', ')

        if (prefixIcon) prefixIcon += ' '

        let response: unknown[] = [`${prefixIcon}[${prefix}] ` + message]

        if (this.canStyleLogMessage) {
            response = [
                `%c${prefixIcon}%c[${prefix}]%c ${message}`,
                `color: ${iconColor}`,
                `color: ${messagePrefixColor}`,
                `color: ${messageColor}`,
            ]
        }

        if ((args?.length ?? 0) >= 1) {
            response.push(...args)
        }

        return response
    }
}
